// Task #1494 — Pro Owner Marketing Spotlight API client.
import { getSharedRefreshedToken, forceSessionExpiredRedirect } from "@/lib/auth-refresh";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

function getImpersonationHeaders(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem("bac_impersonation");
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data.expiresAt < Date.now()) return {};
    if (!data.userId || !data.sessionId) return {};
    return {
      "X-Impersonate-User-Id": data.userId,
      "X-Impersonation-Session-Id": data.sessionId,
    };
  } catch {
    return {};
  }
}

async function authFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getImpersonationHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) {
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        const retryRes = await fetch(`${API_BASE}${path}`, {
          ...init,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshToken}`,
            ...getImpersonationHeaders(),
            ...init?.headers,
          },
        });
        if (retryRes.ok) return retryRes.json();
        const retryBody = await retryRes.json().catch(() => ({}));
        if (retryRes.status === 401 || retryBody.error === "session_expired") {
          forceSessionExpiredRedirect();
        }
        throw new Error(retryBody.error || `API error ${retryRes.status}`);
      }
      // No fresh token — session is truly dead.
      forceSessionExpiredRedirect();
    }
    throw new Error(body.error || `API error ${res.status}`);
  }
  return res.json();
}

export interface SpotlightSocials {
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  facebook?: string;
  x?: string;
}

export interface SpotlightSubmission {
  user_id: string;
  story: string | null;
  photos: string[];
  socials: SpotlightSocials;
  languages: string[];
  availability: string | null;
  preferred_contact: string | null;
  consent: boolean;
  opt_in_status: string;
  featured_at: string | null;
  featured_url: string | null;
  last_sent_at: string | null;
  submitted_at: string;
  updated_at: string;
}

export interface SpotlightCandidate {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  country_code: string | null;
  city: string | null;
  state: string | null;
  tier: string | null;
  tier_started_at: string | null;
  fleet_size: number;
  submitted_at: string | null;
  opt_in_status: string;
  has_submission: boolean;
  featured_at: string | null;
  featured_url: string | null;
  last_sent_at: string | null;
}

export interface SpotlightAggregate {
  submission: SpotlightSubmission | null;
  profile: {
    id: string | number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    country_code: string | null;
    city: string | null;
    state: string | null;
    profile_image: string | null;
    created_at: string | null;
    language: string | null;
  };
  subscription: {
    tier: string | null;
    status: string | null;
    billing_interval: string | null;
    created_at: string | null;
    current_period_end: string | null;
  } | null;
  fleet: Array<{
    id: number;
    make: string;
    model: string;
    year: number;
    photos: string[] | null;
    daily_rate: number | null;
    currency_code: string | null;
    city: string | null;
  }>;
  performance: {
    total_trips: number;
    completed_trips: number;
    avg_rating: number | null;
    review_count: number;
    repeat_renter_rate: number | null;
    response_rate: number | null;
  };
}

export interface SpotlightFormInput {
  story: string;
  photos: string[];
  socials: SpotlightSocials;
  languages: string[];
  availability: string;
  preferred_contact: string;
  consent: boolean;
}

async function uploadPhotoOnce(token: string, file: File | Blob): Promise<Response> {
  return fetch(`${API_BASE}/pro-owner/spotlight/photo/upload`, {
    method: "POST",
    headers: {
      "Content-Type": file.type || "image/jpeg",
      Authorization: `Bearer ${token}`,
      ...getImpersonationHeaders(),
    },
    body: file,
  });
}

export const spotlightApi = {
  getMine: (token: string) =>
    authFetch<{ submission: SpotlightSubmission | null }>("/pro-owner/spotlight/me", token),

  // Raw-body image upload — cannot go through authFetch (JSON content type).
  uploadPhoto: async (token: string, file: File | Blob): Promise<{ url: string }> => {
    let res = await uploadPhotoOnce(token, file);
    if (res.status === 401) {
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        res = await uploadPhotoOnce(freshToken, file);
      } else {
        forceSessionExpiredRedirect();
      }
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as { error?: string }).error || `Upload failed (${res.status})`);
    }
    return res.json();
  },

  saveMine: (token: string, body: SpotlightFormInput) =>
    authFetch<{ ok: boolean }>("/pro-owner/spotlight/me", token, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  adminList: (token: string, filter: "all" | "new" | "featured" = "all") =>
    authFetch<{ candidates: SpotlightCandidate[]; summary: { total: number; new: number; featured: number } }>(
      `/admin/spotlight?filter=${filter}`,
      token,
    ),

  adminGet: (token: string, userId: string) =>
    authFetch<SpotlightAggregate>(`/admin/spotlight/${encodeURIComponent(userId)}`, token),

  adminSendToMarketing: (token: string, userId: string) =>
    authFetch<{ ok: boolean; recipients: string[] }>(`/admin/spotlight/${encodeURIComponent(userId)}/send-to-marketing`, token, {
      method: "POST",
    }),

  adminMarkFeatured: (token: string, userId: string, featured: boolean, featured_url?: string) =>
    authFetch<{ ok: boolean }>(`/admin/spotlight/${encodeURIComponent(userId)}/featured`, token, {
      method: "PATCH",
      body: JSON.stringify({ featured, featured_url: featured_url ?? null }),
    }),
};
